import type {
  ControlPlaneConnectionState,
  ControlPlaneOperationalProjection,
} from '@internal/spassu-code-contract';
import { Box, Chip, Typography, makeStyles } from '@material-ui/core';
import {
  ACTIVITY_CYCLE_LABELS,
  CONNECTION_LABELS,
  EXPIRY_BAND_LABELS,
  formatManagementInstant,
} from './control-plane-management-labels';
import {
  StateDimension,
  useStateDimensionStyles,
} from './ControlPlaneStateDimension';

const useStyles = makeStyles(theme => ({
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(4),
  },
  chipRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  // A styled paragraph rather than a MUI Chip: the state label stays a plain
  // text node that reads and tests like the other dimension values.
  pill: {
    display: 'inline-block',
    margin: 0,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 16,
    border: '1px solid',
    fontWeight: theme.typography.fontWeightMedium as number,
  },
  chipOk: {
    borderColor: theme.palette.success.main,
    color: theme.palette.success.dark,
  },
  chipPending: {
    borderColor: theme.palette.info.main,
    color: theme.palette.info.dark,
  },
  chipProblem: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.dark,
  },
  warning: {
    color: theme.palette.warning.dark,
  },
}));

/** Visual tone only; the textual label is what carries the meaning. */
const CONNECTION_TONE: Record<
  ControlPlaneConnectionState,
  'ok' | 'pending' | 'problem'
> = {
  not_started: 'pending',
  awaiting_pairing: 'pending',
  connecting: 'pending',
  connected: 'ok',
  expired: 'problem',
  revoked: 'problem',
  failed: 'problem',
  unavailable: 'problem',
};

export function ConnectionSummary(props: {
  projection: ControlPlaneOperationalProjection;
  stale: boolean;
}) {
  const classes = useStyles();
  const { activityCycle } = props.projection;
  return (
    <Box className={classes.summary}>
      <ConnectionDimension
        connection={props.projection.connection}
        stale={props.stale}
      />
      <StateDimension
        label="Ciclo da atividade"
        value={ACTIVITY_CYCLE_LABELS[activityCycle]}
      />
    </Box>
  );
}

function ConnectionDimension(props: {
  connection: ControlPlaneOperationalProjection['connection'];
  stale: boolean;
}) {
  const classes = useStyles();
  const dimensionClasses = useStateDimensionStyles();
  const { connection } = props;
  const toneClass = {
    ok: classes.chipOk,
    pending: classes.chipPending,
    problem: classes.chipProblem,
  }[CONNECTION_TONE[connection.state]];
  return (
    <Box className={dimensionClasses.dimension}>
      <Typography variant="overline" component="h3">
        Conexão
      </Typography>
      <Box className={classes.chipRow}>
        <Typography
          component="p"
          variant="body2"
          className={`${classes.pill} ${toneClass}`}
        >
          {CONNECTION_LABELS[connection.state]}
        </Typography>
        {props.stale ? (
          <Chip
            size="small"
            label="Leitura desatualizada"
            role="status"
            aria-label="Leitura desatualizada; mostrando o último estado conhecido"
          />
        ) : null}
      </Box>
      <ConnectionExpiry connection={connection} />
    </Box>
  );
}

function ConnectionExpiry({
  connection,
}: {
  connection: ControlPlaneOperationalProjection['connection'];
}) {
  const classes = useStyles();
  const expiresAt = formatManagementInstant(connection.expiresAt);
  const band =
    connection.expiryBand === 'none'
      ? null
      : EXPIRY_BAND_LABELS[connection.expiryBand];
  return (
    <>
      {expiresAt ? (
        <Typography variant="body2">Válida até {expiresAt}</Typography>
      ) : null}
      {band ? (
        <Typography
          variant="caption"
          className={
            connection.expiryBand === 'expired' ? undefined : classes.warning
          }
        >
          {band}
        </Typography>
      ) : null}
    </>
  );
}
